/**
 * The workspace's company profile (lib/workspace/company.ts): fields on the workspace record, written by
 * the owner only (the workspace's update rule).
 */
import {
  COMPANY_FIELDS,
  companyOf,
  workspaceField,
  type Company,
  type CompanyFormValues,
} from "@/lib/workspace/company";
import { toLocale } from "@/i18n/languages";
import { countryPreset, localDefaults, NORTH_AMERICAN } from "@/lib/invoice/country-presets";
import { LABEL_PRESETS, US_LABELS } from "@/lib/invoice/label-presets";
import { labelLanguage } from "@/lib/invoice/quote-labels";
import { isPresetVatNote, vatNoteFor } from "@/lib/invoice/vat-notes";
import { pb } from "@/lib/core/pocketbase";
import { getDefaultInvoice, toFormValues } from "./invoices";
import { currentWorkspaceId } from "./workspaces";

/** The company of a workspace (the current one unless given). */
export async function getCompany(workspaceId = currentWorkspaceId()): Promise<Company> {
  return companyOf(await pb.collection("workspaces").getOne(workspaceId));
}

/** Saves the profile; saving also confirms details a migration derived (companyReviewed). */
export async function saveCompany(values: CompanyFormValues): Promise<Company> {
  const body: Record<string, unknown> = { companyReviewed: true };
  for (const field of COMPANY_FIELDS) body[workspaceField(field)] = values[field];
  return companyOf(await pb.collection("workspaces").update(currentWorkspaceId(), body));
}

/** Uploads (or with `null` removes) the logo right away; returns the stored file name. */
export async function setCompanyLogo(file: File | null): Promise<string> {
  const record = await pb
    .collection("workspaces")
    .update(currentWorkspaceId(), { companyLogo: file });
  return String(record.companyLogo ?? "");
}

/** The VAT rate new invoices start with where there is no preset yet (#92): none in the US. */
const BASE_RATES: Record<string, number> = { US: 0, CA: 5 };

/**
 * A new company country brings its standard VAT rate and the wording of its no-VAT notes to the
 * invoice settings (a note the user wrote stays); outside the euro area also its currency, date
 * and number format; in the US and Canada the North American labels ("Bill to:", "Invoice #"),
 * as long as the English labels are the built-in ones. Returns the rate and the currency, or null
 * when the country changes nothing.
 */
export async function applyCountry(
  country: string,
  appLanguage: string,
): Promise<{ tax: number; currency?: string } | null> {
  const preset = countryPreset(country);
  const tax = preset ? preset.rates[0]! : BASE_RATES[country];
  if (tax === undefined) return null;
  const defaults = await getDefaultInvoice();
  const values = toFormValues(defaults);
  const changes: Record<string, string | number> = { tax, ...localDefaults(country) };
  if (
    preset &&
    values.vatMode !== "standard" &&
    (!values.vatNote || isPresetVatNote(values.vatNote))
  ) {
    const language = labelLanguage(values, toLocale(appLanguage) ?? "en");
    changes.vatNote = vatNoteFor(values.vatMode, language, country);
  }
  const builtIn = (Object.keys(US_LABELS) as (keyof typeof US_LABELS)[]).every(
    (name) => values[name] === LABEL_PRESETS.en[name],
  );
  if (NORTH_AMERICAN.includes(country) && builtIn) Object.assign(changes, US_LABELS);
  await pb.collection("invoices").update(defaults.id, changes);
  return { tax, currency: localDefaults(country)?.currency };
}
